import { windowStore } from 'util/windowStore';
import {
  cacheKey,
  USER_NAME_LENGTH_LIMIT,
  businessTypeOption,
  pendoProductAreas,
  APP_NAME,
} from 'appConstants';
import { sumoLogger } from 'sumoLogger';

const cloudEnvironment = windowStore.get(cacheKey.CLOUD_ENV);

const DEMO_TYPES = ['DEMO_NO_UL_SYNC', 'DEMO_YES_UL_SYNC'];
export const isDemoCustomer = (user) => {
  const { internalCustomerType } = user.customerInfo;
  return DEMO_TYPES.includes(internalCustomerType);
};

export const initializePendo = (user, customerInfo) => {
  if (typeof pendo === 'undefined') {
    sumoLogger.log({
      error: {
        message: 'Pendo Error: Failed To Initialize',
        userName: user.userName,
      },
    });
    return;
  }

  const visitor = {
    id: `ACS-${cloudEnvironment}-${user.id}`,
    userName: user.userName,
    role: user.customerIds[0] ? user.customerIds[0].role : '',
  };
  const account = {
    id: user?.icimsIntegration || `ACS-${cloudEnvironment}-${user.customerId}`,
    accountName: customerInfo.customerName,
    // eslint-disable-next-line camelcase -- Pendo expects underscore [TXTE-26818]
    customer_name: `${cloudEnvironment}_${customerInfo.customerName}`,
    salesforceAccountId: customerInfo.textRecruitSalesForceId || '',
    product: user.customerIds[0] ? user.customerIds[0].product : '',
  };

  window.pendo.initialize({ visitor, account });
};

export const webReactOptOutTracker = (user) => {
  try {
    if (isDemoCustomer(user)) {
      return;
    }
    window.pendo.track('User Opted Out Of WebReact', {
      app: APP_NAME,
      customerId: user.customerId,
      userName: user.userName,
      productArea: pendoProductAreas.TEXT_ENGAGEMENT,
      method: 'WR Opt-out By Toggle Switch',
    });
  } catch (err) {
    sumoLogger.log({
      error: err,
      additionalInfo: {
        message: 'Pendo Error: Failed To Track Web-React Opt-Out',
        userName: user.userName,
      },
    });
  }
};

export const updateLoginPendoTrack = (user) => {
  try {
    const loginTrackKey = 'hasLoggedInTrackForPendo';
    if (isDemoCustomer(user) || sessionStorage.getItem(loginTrackKey)) {
      return;
    }
    window.pendo.track('User Logged In', {
      app: APP_NAME,
      customerId: user.customerId,
      userName: user.userName,
      productArea: pendoProductAreas.TEXT_ENGAGEMENT,
      date: new Date().toISOString(),
    });

    sessionStorage.setItem(loginTrackKey, 'true');
  } catch (err) {
    sumoLogger.log({
      error: err,
      additionalInfo: {
        message: 'Pendo Error: Failed To Track Login',
        userName: user.userName,
      },
    });
  }
};

export const reformatDlcFormData = (formData) => {
  const businessPhysicalAddress = {
    country: formData.country,
    city: formData.city,
    state: formData.state,
    postalCode: formData.postalCode?.trim(),
    streetAddress1: formData.streetAddress1,
    streetAddress2: formData.streetAddress2,
  };
  const peopleToContact = [
    {
      firstName: formData.firstNamePOC1,
      lastName: formData.lastNamePOC1,
      email: formData.emailPOC1,
      businessTitle: formData.titlePOC1,
      jobPosition: formData.jobPositionPOC1,
      countryCode: formData.countryCodePOC1,
      phoneNumber: formData.phoneNumberPOC1,
    },
  ];
  const newFormData = {
    siteName: formData.siteName,
    businessType: formData.businessType,
    companyStatus: formData.companyStatus,
    stockTicker: formData.stockTicker,
    stockExchange: formData.stockExchange,
    brandRepresentativeEmail: formData.brandRepresentativeEmail,
    businessRegistrationNumber: formData.businessRegistrationNumber,
    website: formData.website,
    campaignType: formData.campaignType,
    campaignDescription: formData.campaignDescription,
    messageFlow: formData.messageFlow,
    sampleMessage: formData.sampleMessage,
    businessIndustry: formData.businessIndustry,
    businessRegistrationType: formData.businessRegistrationType,
    socialMediaUrl: formData.socialMediaUrl,
    regionsOfOperations: formData.regionsOfOperations,
    requestToSubmit: formData.requestToSubmit,
  };

  if (!formData.disablePOC2) {
    peopleToContact.push({
      firstName: formData.firstNamePOC2,
      lastName: formData.lastNamePOC2,
      email: formData.emailPOC2,
      businessTitle: formData.titlePOC2,
      jobPosition: formData.jobPositionPOC2,
      countryCode: formData.countryCodePOC2,
      phoneNumber: formData.phoneNumberPOC2,
    });
  }

  return { ...newFormData, businessPhysicalAddress, peopleToContact };
};

export const getLengthLimitedUsername = (userName) => {
  if (userName.length > USER_NAME_LENGTH_LIMIT) {
    return userName.split('@')[0];
  }
  return userName;
};

export const businessTypes = [
  {
    value: 'Sole Proprietorship',
    label: 'Sole Proprietorship',
    key: 'sole_proprietorship',
  },
  {
    value: 'Partnership',
    label: 'Partnership',
    key: 'partnership',
  },
  {
    value: 'Corporation',
    label: 'Corporation',
    key: 'corporation',
  },
  {
    value: 'Co-operative',
    label: 'Co-Operative',
    key: 'coOperative',
  },
  {
    value: 'Limited Liability Corporation',
    label: 'Limited Liability Corporation',
    key: 'llc',
  },
  {
    value: 'Non-profit Corporation',
    label: 'Non-profit Corporation',
    key: 'nonProfit',
  },
];

export const regionsOfOperation = [
  {
    label: 'Asia',
    value: 'ASIA',
  },
  {
    label: 'Africa',
    value: 'AFRICA',
  },
  {
    label: 'Europe',
    value: 'EUROPE',
  },
  {
    label: 'Latin America',
    value: 'LATIN_AMERICA',
  },
  {
    label: 'USA & Canada',
    value: 'USA_AND_CANADA',
  },
];

export const companyStatusTypes = [
  {
    value: 'private',
    label: 'Private',
    key: 'private',
  },
  {
    value: 'public',
    label: 'Public',
    key: 'public',
  },
];

export const jobPositionTypes = [
  {
    value: 'Director',
    label: 'Director',
    key: 'director',
  },
  {
    value: 'VP',
    label: 'VP',
    key: 'vp',
  },
  {
    value: 'GM',
    label: 'GM',
    key: 'gm',
  },
  {
    value: 'General Counsel',
    label: 'General Counsel',
    key: 'general_counsel',
  },
  {
    value: 'CEO',
    label: 'CEO',
    key: 'ceo',
  },
  {
    value: 'CFO',
    label: 'CFO',
    key: 'cfo',
  },
  {
    value: 'Other',
    label: 'Other',
    key: 'other',
  },
];

export const businessIndustries = [
  {
    value: 'AUTOMOTIVE',
    label: 'Automotive',
    key: 'automotive',
  },
  {
    value: 'AGRICULTURE',
    label: 'Agriculture',
    key: 'agriculture',
  },
  {
    value: 'BANKING',
    label: 'Banking',
    key: 'banking',
  },
  {
    value: 'CONSUMER',
    label: 'Consumer',
    key: 'consumer',
  },
  {
    value: 'EDUCATION',
    label: 'Education',
    key: 'education',
  },
  {
    value: 'ENGINEERING',
    label: 'Engineering',
    key: 'engineering',
  },
  {
    value: 'ENERGY',
    label: 'Energy',
    key: 'energy',
  },
  {
    value: 'OIL_AND_GAS',
    label: 'Oil & Gas',
    key: 'oilAndGas',
  },
  {
    value: 'FAST_MOVING_CONSUMER_GOODS',
    label: 'Fast moving consumer goods',
    key: 'fastMovingConsumerGoods',
  },
  {
    value: 'FINANCIAL',
    label: 'Financial',
    key: 'financial',
  },
  {
    value: 'FINTECH',
    label: 'Fintech',
    key: 'fintech',
  },
  {
    value: 'FOOD_AND_BEVERAGE',
    label: 'Food & Beverage',
    key: 'foodAndBeverage',
  },
  {
    value: 'GOVERNMENT',
    label: 'Government',
    key: 'government',
  },
  {
    value: 'HEALTHCARE',
    label: 'Healthcare',
    key: 'healthcare',
  },
  {
    value: 'HOSPITALITY',
    label: 'Hospitality',
    key: 'hospitality',
  },
  {
    value: 'INSURANCE',
    label: 'Insurance',
    key: 'insurance',
  },
  {
    value: 'LEGAL',
    label: 'Legal',
    key: 'legal',
  },
  {
    value: 'MANUFACTURING',
    label: 'Manufacturing',
    key: 'manufacturing',
  },
  {
    value: 'MEDIA',
    label: 'Media',
    key: 'media',
  },

  {
    value: 'ONLINE',
    label: 'Online',
    key: 'online',
  },
  {
    value: 'RAW_MATERIALS',
    label: 'Raw materials',
    key: 'rawMaterials',
  },
  {
    value: 'REAL_ESTATE',
    label: 'Real Estate',
    key: 'realEstate',
  },
  {
    value: 'RELIGION',
    label: 'Religion',
    key: 'retail',
  },
  {
    value: 'RETAIL',
    label: 'Retail',
    key: 'retail',
  },
  {
    value: 'JEWELRY',
    label: 'Jewelry',
    key: 'jewlery',
  },
  {
    value: 'TECHNOLOGY',
    label: 'Technology',
    key: 'technology',
  },

  {
    value: 'TELECOMMUNICATIONS',
    label: 'Telecommunication',
    key: 'telecommunication',
  },
  {
    value: 'TRANSPORTATION',
    label: 'Transportation',
    key: 'transportation',
  },
  {
    value: 'TRAVEL',
    label: 'Travel',
    key: 'travel',
  },

  {
    value: 'ELECTRONICS',
    label: 'Electronics',
    key: 'electronics',
  },
  {
    value: 'NOT_FOR_PROFIT',
    label: 'Not for profit',
    key: 'notForProfit',
  },
];

export const campaignTypes = [
  {
    value: 'ACCOUNT_NOTIFICATION',
    key: 'notifications_desc',
    desc: 'notifications_desc',
    label:
      'Account Notifications: Notifications about the status of an account or related to being a part of an account. Account refers to a candidate' +
      " or employee's status with your organization.",
  },
  {
    value: 'CUSTOMER_CARE',
    key: 'customer_care_desc',
    desc: 'customer_care_desc',
    label:
      'Customer Care: Support, account management, and other avenues of customer interaction. Customer refers to a candidate or employee.',
  },
  {
    value: 'LOW_VOLUME',
    key: 'low_volume_mixed_desc',
    desc: 'low_volume_mixed_desc',
    label:
      'Low Volume Mixed: A campaign that covers multiple use cases such as recruitment marketing promotions and candidate/ employee notifications.' +
      'Primarily for small businesses.',
  },
  {
    value: 'MIXED',
    key: 'mixed_desc',
    desc: 'mixed_Desc',
    label:
      'Mixed: A campaign that covers multiple use cases such as recruitment marketing promotions and candidate/ employee notifications.',
  },
];

export const businessRegistrationTypes = [
  {
    value: 'EIN',
    label: 'USA: Employer Identification Number (EIN)',
    key: businessTypeOption.EIN,
  },
  {
    value: 'CCN',
    label: 'Canada: Canadian Corporation Number (CCN)',
    key: 'CCN',
  },
  {
    value: 'CN',
    label: 'Great Britain: Company Number',
    key: 'CN',
  },

  {
    value: 'ACN',
    label: 'Australia: Company Number from ASIC (ACN)',
    key: 'ACN',
  },
  {
    value: 'CIN',
    label: 'India: Corporate Identity Number',
    key: 'CIN',
  },
  {
    value: 'VAT',
    label: 'Estonia: VAT Number',
    key: 'VAT',
  },
  {
    value: 'VATRN',
    label: 'Romania: VAT Registration Number',
    key: 'VATRN',
  },
  {
    value: 'RN',
    label: 'Israel: Registration Number',
    key: 'RN',
  },
  {
    value: 'Other',
    label: 'Other',
    key: 'Other',
  },
];

export const stockExchanges = [
  {
    key: 'AMEX',
    label: 'AMEX:American Stock Exchange',
    value: 'amex',
  },
  {
    key: 'AMX',
    label: 'AMX:Armenia Securities Exchange',
    value: 'amx',
  },
  {
    key: 'ASX',
    label: 'ASX:Australian Securities Exchange',
    value: 'asx',
  },
  {
    key: 'B3',
    label: 'B3:Brazil Stock Exchange',
    value: 'b3',
  },
  {
    key: 'BME',
    label: 'BME:Bolsas y Mercados Españoles',
    value: 'bme',
  },
  {
    key: 'BSE',
    label: 'BSE:Bombay Stock Exchange',
    value: 'bse',
  },
  {
    key: 'FRA',
    label: 'FRA:Frankfut Stock Exchange',
    value: 'fra',
  },
  {
    key: 'ICEX',
    label: 'ICEX:Indian Commodity Exchange',
    value: 'icex',
  },
  {
    key: 'JPX',
    label: 'JPX:Japan Exchange Group',
    value: 'jpx',
  },
  {
    key: 'JSE',
    label: 'JSE:Jamacia Stock Exchange',
    value: 'jse',
  },
  {
    key: 'KRX',
    label: 'KRX:Korea Exchange',
    value: 'krx',
  },
  {
    key: 'LON',
    label: 'LON:London Stock Exchange',
    value: 'lon',
  },
  {
    key: 'NASDAQ',
    label: 'NASDAQ:National Association of Securities Dealers Automated Quotations',
    value: 'nasdaq',
  },
  {
    key: 'NSE',
    label: 'NSE:National Stock Exchange of India Ltd',
    value: 'nse',
  },
  {
    key: 'NYSE',
    label: 'NYSE: New York Stock Exchange',
    value: 'nyse',
  },
  {
    key: 'OMX',
    label: 'OMX:NASDAQ Stockholm',
    value: 'omx',
  },
  {
    key: 'SEHK',
    label: 'SEHK:Stock Exchange of Hong Kong',
    value: 'sehk',
  },
  {
    key: 'SGX',
    label: 'SGX:Singapore Exchange Limited',
    value: 'sgx',
  },
  {
    key: 'SGX',
    label: 'SGX:Singapore Exchange Limited',
    value: 'sgx',
  },
  {
    key: 'SSE',
    label: 'SSE:Shanghai Stock Exchange',
    value: 'sse',
  },
  {
    key: 'STO',
    label: 'STO:Stockholm Stock Exchange',
    value: 'sto',
  },
  {
    key: 'SWX',
    label: 'SWX:Swiss Stock Exchange',
    value: 'sgx',
  },
  {
    key: 'SZSE',
    label: 'SZSE:Shenzhen Stock Exchange',
    value: 'szse',
  },
  {
    key: 'TSX',
    label: 'TSX:Toronto Stock Exchange',
    value: 'tsx',
  },
  {
    key: 'TWSE',
    label: 'TWSE:Taiwan Stock Exchange',
    value: 'twse',
  },
  {
    key: 'VSE',
    label: 'VSE:Vadodara Stock Exchange Limited',
    value: 'vse',
  },
  {
    key: 'Other',
    label: 'Other',
    value: 'other',
  },
];

export const getUserNavbarPreferences = (userName) => {
  const userNavbarPreferences = windowStore.getUserNavPreference();
  return userNavbarPreferences ? userNavbarPreferences[userName] : false;
};
